import type { WritableDeep } from 'type-fest';

import type { PrintStyleId } from '@e-socna/data-layer';

import { useReferencesContext } from '../../../contexts/ReferencesContext/hooks/useReferencesContext.hook';
import type { Getters } from '../../../components/DocumentEditor/types/props.type';
import type { DocumentEditorCmp_RecursiveSection } from '../../../components/DocumentEditor/fragments/recursive-section.fragment';

import { MissionReportPage_ReportVersions_QueryDocument } from '../queries/report-versions.query';
import type { MissionReportWithItsInfo } from '../types/mission-report-with-its-info.type';

export const useGetters: () => Getters<MissionReportWithItsInfo> = () => {
  const { fileDirectoryCategoriesCodeMap, reportFileCategoriesCodeMap } =
    useReferencesContext();

  return {
    editable: ((report: Readonly<MissionReportWithItsInfo>) => ({
      virtualRoot: report.virtualRoot,
      code: report.code,
      title: report.title,
    })) as <W extends boolean = false>(
      editable: W extends false
        ? MissionReportWithItsInfo
        : WritableDeep<MissionReportWithItsInfo>,
    ) => Readonly<{
      virtualRoot: W extends false
        ? DocumentEditorCmp_RecursiveSection
        : WritableDeep<DocumentEditorCmp_RecursiveSection>;
      title: string;
      code: string;
    }>,
    printing: (report: Readonly<MissionReportWithItsInfo>) => {
      const {
        printing: {
          printSettings: { addFooters, addHeaders, printStyleId },
          pdfPrints,
          pdfPrintsIncrement,
        },
      } = report;

      const printSettings = {
        addFooters,
        addHeaders,
        printStyleId: printStyleId as PrintStyleId,
      };

      return {
        __typename: 'ReportPrinting' as const,
        printSettings: {
          __typename: 'PrintSettings' as const,
          ...printSettings,
        },
        pdfPrints,
        pdfPrintsIncrement,
      };
    },
    storage: (report: Readonly<MissionReportWithItsInfo>) => ({
      directoryCategoryId: fileDirectoryCategoriesCodeMap.Report._id,
      entityId: report._id,
      imageCategory: reportFileCategoriesCodeMap.TextEditorImages,
      pdfCategory: reportFileCategoriesCodeMap.PDFPrints,
    }),
    versions: () => ({
      queryDocument: MissionReportPage_ReportVersions_QueryDocument,
    }),
  };
};
